import { z } from "zod";
import {
  edgesSchema,
  edges,
  rotateEdges,
  cropSegments,
  markSettingsSchema,
  type Rect,
  type Segment,
  type Edges,
} from "./production";
import type { ScenePage } from "./scene";
export const sheetStockSchema = z.object({
  material: z.string().max(200),
  costPerSheet: z.number().min(0).max(1000000).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  weightGsm: z.number().positive().max(5000).optional(),
  caliperMm: z.number().positive().max(100).optional(),
  color: z.string().max(100).optional(),
  coating: z.string().max(100).optional(),
  finish: z.string().max(200).optional(),
  grain: z.enum(["unspecified", "short", "long"]).optional(),
  sides: z.enum(["simplex", "duplex"]).optional(),
  process: z
    .enum(["digital", "offset", "inkjet", "screen", "other"])
    .optional(),
  press: z.string().max(200).optional(),
  supplier: z.string().max(200).optional(),
  sku: z.string().max(100).optional(),
  recycledPercent: z.number().min(0).max(100).optional(),
  certifiedSustainable: z.boolean().optional(),
  notes: z.string().max(2000).optional(),
});
export type SheetStock = z.infer<typeof sheetStockSchema>;
export const impositionSchema = z.object({
  stock: sheetStockSchema.optional(),
  enabled: z.boolean().default(false),
  sheetWidth: z.number().positive().max(14400).default(612),
  sheetHeight: z.number().positive().max(14400).default(792),
  columns: z.number().int().min(1).max(20).default(2),
  rows: z.number().int().min(1).max(20).default(5),
  gap: z.number().min(0).max(14400).default(12),
  margin: z.number().min(0).max(14400).default(18),
  copies: z.number().int().min(1).max(100).default(10),
  rotate: z.boolean().default(false),
  gridMode: z.enum(["exact", "auto"]).default("exact"),
  sourcePage: z.string().min(1).default("all"),
  rotation: z.enum(["none", "quarter-turn", "auto"]).optional(),
  spacing: z.enum(["fixed", "even"]).default("fixed"),
  gapX: z.number().min(0).max(14400).optional(),
  gapY: z.number().min(0).max(14400).optional(),
  margins: edgesSchema.optional(),
  alignX: z.enum(["start", "center", "end"]).default("start"),
  alignY: z.enum(["start", "center", "end"]).default("start"),
  partialRow: z.enum(["grid", "align"]).default("grid"),
  duplex: z
    .enum(["mirror-columns", "mirror-sheet", "none"])
    .default("mirror-columns"),
  itemBleed: z
    .union([
      z.number().min(0).max(144),
      edgesSchema.refine((e) => Object.values(e).every((v) => v <= 144)),
    ])
    .optional(),
  cropMarks: markSettingsSchema
    .extend({ enabled: z.boolean().default(false) })
    .default({ enabled: false, length: 9, offset: 3, strokeWidth: 0.5 }),
});
export type Imposition = z.infer<typeof impositionSchema>;
export interface ItemPlacement {
  copyIndex: number;
  row: number;
  column: number;
  rotation: 0 | 90;
  footprint: Rect;
  trim: Rect;
  bleed: Rect;
  marks: Segment[];
}
export interface SheetPlan {
  id: string;
  name: string;
  sourcePageId: string;
  sourceIndex: number;
  chunk: number;
  width: number;
  height: number;
  columns: number;
  rows: number;
  gapX: number;
  gapY: number;
  grid: Rect;
  back: boolean;
  items: ItemPlacement[];
}
export interface ImpositionPlan {
  version: 1;
  copies: number;
  sourcePages: string[];
  capacity: number;
  rotation: 0 | 90;
  sheets: SheetPlan[];
  repeatedNodes: number;
  stock?: SheetStock;
  physicalSheets?: number;
  materialCost?: { amount: number; currency: string };
}
type Layout = {
  rotation: 0 | 90;
  columns: number;
  rows: number;
  capacity: number;
  cellWidth: number;
  cellHeight: number;
  gapX: number;
  gapY: number;
  gridWidth: number;
  gridHeight: number;
  offsetX: number;
  offsetY: number;
  itemSizes: {
    width: number;
    height: number;
    bleed: Edges;
    footWidth: number;
    footHeight: number;
  }[];
};
const aligned = (space: number, align: Imposition["alignX"]) =>
  align === "center" ? space / 2 : align === "end" ? space : 0;
export function planImposition(
  pages: Pick<
    ScenePage,
    "id" | "name" | "width" | "height" | "nodes" | "background" | "production"
  >[],
  input: Imposition,
): ImpositionPlan {
  const cfg = impositionSchema.parse(input),
    selected = pages
      .map((page, index) => ({ page, index }))
      .filter(
        ({ page }) =>
          cfg.sourcePage === "all" ||
          page.id === cfg.sourcePage ||
          page.id.startsWith(`${cfg.sourcePage}_continuation_`),
      );
  if (!selected.length) throw new Error("Selected source page is unavailable");
  const margins = edges(cfg.margins ?? cfg.margin),
    availableWidth = cfg.sheetWidth - margins.left - margins.right,
    availableHeight = cfg.sheetHeight - margins.top - margins.bottom;
  if (availableWidth <= 0 || availableHeight <= 0)
    throw new Error("Sheet margins leave no usable area");
  const clearance = cfg.cropMarks.enabled
    ? cfg.cropMarks.length + cfg.cropMarks.offset
    : 0;
  const layout = (rotation: 0 | 90): Layout | undefined => {
    const itemSizes = selected.map(({ page }) => {
      let bleed = edges(cfg.itemBleed ?? page.production.bleed);
      if (rotation) bleed = rotateEdges(bleed);
      const width = rotation ? page.height : page.width,
        height = rotation ? page.width : page.height;
      return {
        width,
        height,
        bleed,
        footWidth: width + bleed.left + bleed.right + 2 * clearance,
        footHeight: height + bleed.top + bleed.bottom + 2 * clearance,
      };
    });
    const cellWidth = Math.max(...itemSizes.map((s) => s.footWidth)),
      cellHeight = Math.max(...itemSizes.map((s) => s.footHeight));
    const initialX = cfg.gapX ?? cfg.gap,
      initialY = cfg.gapY ?? cfg.gap;
    let columns = cfg.columns,
      rows = cfg.rows;
    if (cfg.gridMode === "auto") {
      columns = Math.min(
        100,
        cfg.copies,
        Math.floor((availableWidth + initialX) / (cellWidth + initialX) + 1e-9),
      );
      const maxRows = Math.min(
        100,
        Math.floor(
          (availableHeight + initialY) / (cellHeight + initialY) + 1e-9,
        ),
      );
      rows = Math.min(maxRows, Math.ceil(cfg.copies / Math.max(1, columns)));
    }
    if (
      !columns ||
      !rows ||
      columns * cellWidth + (columns - 1) * initialX > availableWidth + 1e-7 ||
      rows * cellHeight + (rows - 1) * initialY > availableHeight + 1e-7
    )
      return;
    const gapX =
        cfg.spacing === "even" && columns > 1
          ? (availableWidth - columns * cellWidth) / (columns - 1)
          : initialX,
      gapY =
        cfg.spacing === "even" && rows > 1
          ? (availableHeight - rows * cellHeight) / (rows - 1)
          : initialY,
      gridWidth = columns * cellWidth + (columns - 1) * gapX,
      gridHeight = rows * cellHeight + (rows - 1) * gapY;
    return {
      rotation,
      columns,
      rows,
      capacity: columns * rows,
      cellWidth,
      cellHeight,
      gapX,
      gapY,
      gridWidth,
      gridHeight,
      offsetX: margins.left + aligned(availableWidth - gridWidth, cfg.alignX),
      offsetY: margins.top + aligned(availableHeight - gridHeight, cfg.alignY),
      itemSizes,
    };
  };
  const requested = cfg.rotation ?? (cfg.rotate ? "quarter-turn" : "none"),
    candidates = (
      requested === "auto"
        ? [layout(0), layout(90)]
        : [layout(requested === "quarter-turn" ? 90 : 0)]
    )
      .filter((l): l is Layout => !!l)
      .sort(
        (a, b) =>
          Math.min(cfg.copies, b.capacity) - Math.min(cfg.copies, a.capacity) ||
          a.rotation - b.rotation,
      ),
    chosen = candidates[0];
  if (!chosen)
    throw new Error(
      "Imposition grid does not fit the sheet including bleed and crop marks",
    );
  const sheetsCount = Math.ceil(cfg.copies / chosen.capacity) * selected.length,
    repeatedNodes =
      selected.reduce(
        (sum, { page }) =>
          sum +
          page.nodes.length +
          (page.background ? 1 : 0) +
          (cfg.cropMarks.enabled ? 8 : 0),
        0,
      ) * cfg.copies;
  if (sheetsCount > 100 || repeatedNodes > 20000)
    throw new Error("Imposition exceeds 100-sheet or 20,000-layer budget");
  const sheets: SheetPlan[] = [];
  for (let chunk = 0; chunk < cfg.copies; chunk += chosen.capacity) {
    for (let side = 0; side < selected.length; side++) {
      const { page, index } = selected[side],
        size = chosen.itemSizes[side],
        back =
          cfg.sourcePage === "all" && side % 2 === 1 && cfg.duplex !== "none",
        count = Math.min(chosen.capacity, cfg.copies - chunk),
        occupiedRows = Math.ceil(count / chosen.columns),
        usedHeight =
          occupiedRows * chosen.cellHeight + (occupiedRows - 1) * chosen.gapY,
        top =
          chosen.offsetY +
          (cfg.partialRow === "align"
            ? aligned(chosen.gridHeight - usedHeight, cfg.alignY)
            : 0),
        items: ItemPlacement[] = [];
      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / chosen.columns),
          col = i % chosen.columns,
          rowCount = Math.min(chosen.columns, count - row * chosen.columns),
          rowWidth = rowCount * chosen.cellWidth + (rowCount - 1) * chosen.gapX,
          rowOffset =
            cfg.partialRow === "align"
              ? aligned(chosen.gridWidth - rowWidth, cfg.alignX)
              : 0;
        let x =
          chosen.offsetX +
          rowOffset +
          col * (chosen.cellWidth + chosen.gapX) +
          (chosen.cellWidth - size.footWidth) / 2;
        const y =
          top +
          row * (chosen.cellHeight + chosen.gapY) +
          (chosen.cellHeight - size.footHeight) / 2;
        if (back)
          x =
            cfg.duplex === "mirror-sheet"
              ? cfg.sheetWidth - x - size.footWidth
              : chosen.offsetX +
                chosen.gridWidth -
                (x - chosen.offsetX) -
                size.footWidth;
        const footprint: Rect = {
            x,
            y,
            width: size.footWidth,
            height: size.footHeight,
          },
          trim: Rect = {
            x: x + clearance + size.bleed.left,
            y: y + clearance + size.bleed.top,
            width: size.width,
            height: size.height,
          },
          bleed: Rect = {
            x: trim.x - size.bleed.left,
            y: trim.y - size.bleed.top,
            width: size.width + size.bleed.left + size.bleed.right,
            height: size.height + size.bleed.top + size.bleed.bottom,
          };
        items.push({
          copyIndex: chunk + i,
          row,
          column: back ? chosen.columns - 1 - col : col,
          rotation: chosen.rotation,
          footprint,
          trim,
          bleed,
          marks: cfg.cropMarks.enabled
            ? cropSegments(
                trim,
                bleed,
                cfg.cropMarks.length,
                cfg.cropMarks.offset,
              )
            : [],
        });
      }
      sheets.push({
        id: `sheet_${chunk}_${index}`,
        name: `Sheet ${Math.floor(chunk / chosen.capacity) + 1} · ${page.name}`,
        sourcePageId: page.id,
        sourceIndex: index,
        chunk,
        width: cfg.sheetWidth,
        height: cfg.sheetHeight,
        columns: chosen.columns,
        rows: chosen.rows,
        gapX: chosen.gapX,
        gapY: chosen.gapY,
        grid: {
          x:
            back && cfg.duplex === "mirror-sheet"
              ? cfg.sheetWidth - chosen.offsetX - chosen.gridWidth
              : chosen.offsetX,
          y: top,
          width: chosen.gridWidth,
          height: chosen.gridHeight,
        },
        back,
        items,
      });
    }
  }
  const physicalSheets =
    cfg.stock?.sides === "duplex"
      ? new Set(
          sheets.map(
            (sheet) => `${Math.floor(sheet.sourceIndex / 2)}:${sheet.chunk}`,
          ),
        ).size
      : sheets.length;
  return {
    version: 1,
    copies: cfg.copies,
    sourcePages: selected.map((s) => s.page.id),
    capacity: chosen.capacity,
    rotation: chosen.rotation,
    sheets,
    repeatedNodes,
    ...(cfg.stock
      ? {
          stock: cfg.stock,
          physicalSheets: physicalSheets,
          ...(cfg.stock.costPerSheet !== undefined
            ? {
                materialCost: {
                  amount:
                    Math.round(
                      physicalSheets * cfg.stock.costPerSheet * 1000000,
                    ) / 1000000,
                  currency: cfg.stock.currency || "USD",
                },
              }
            : {}),
        }
      : {}),
  };
}
